import React, { useEffect } from 'react';
import { PrimeProcess } from '../../types/primeModel';
import { ProcessType, DemandFlowType } from '../../types/enums';
import styles from './ProcessForm.module.css';
import { ProcessCentricProcess, ProcessStateView } from '../../types/primeModelViews';
import { useState } from 'react';
import { SkillSelector } from '../Skills/SkillSelector';
import { apiClient } from '../../services/apiClient';

interface ProcessFormProps {
  process: PrimeProcess;
  isEditing: boolean;
  onChange: (
    field: keyof PrimeProcess,
    value: string | number | boolean | Record<string, unknown> | null
  ) => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onEdit: () => void;
  processCentricView?: ProcessCentricProcess | null;
  jsonSectionCollapsed?: boolean;
  onToggleJsonSection?: () => void;
  viewLoading?: boolean;
  viewError?: string | null;
}

export const ProcessForm: React.FC<ProcessFormProps> = ({
  process,
  isEditing,
  onChange,
  onSave,
  onCancel,
  onDelete,
  onEdit,
  processCentricView,
  jsonSectionCollapsed = true,
  onToggleJsonSection,
  viewLoading,
  viewError,
}) => {
  // State for managing process skills
  const [processSkills, setProcessSkills] = useState<any[]>([]);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);

  // Load process skills when component mounts or process changes
  useEffect(() => {
    if (process.id && process.prime_artifact_ref) {
      loadProcessSkills();
    }
  }, [process.id, process.prime_artifact_ref]);

  const loadProcessSkills = async () => {
    if (!process.id || !process.prime_artifact_ref) return;
    
    try {
      setLoadingSkills(true);
      const response = await apiClient.get(
        `/prime-artifacts/${process.prime_artifact_ref}/processes/${process.id}/skills`
      );
      const skills = response.data || [];
      setProcessSkills(skills);
      setSelectedSkills(skills.map((s: any) => s.skill_parameter_ref));
    } catch (error) {
      console.error('Error loading process skills:', error);
    } finally {
      setLoadingSkills(false);
    }
  };

  const handleSkillsChange = async (skillIds: string | string[]) => {
    const skillArray = Array.isArray(skillIds) ? skillIds : [skillIds];
    setSelectedSkills(skillArray);

    // If editing, immediately sync with backend
    if (isEditing && process.id && process.prime_artifact_ref) {
      try {
        // Get current skill IDs
        const currentSkillIds = processSkills.map(s => s.skill_parameter_ref);
        
        // Find skills to add
        const skillsToAdd = skillArray.filter(id => !currentSkillIds.includes(id));
        
        // Find skills to remove
        const skillsToRemove = currentSkillIds.filter(id => !skillArray.includes(id));

        // Add new skills
        for (const skillId of skillsToAdd) {
          await apiClient.post(
            `/prime-artifacts/${process.prime_artifact_ref}/processes/${process.id}/skills`,
            {
              name: `Skill ${skillId}`,
              skill_parameter_ref: skillId,
              prime_process_ref: process.id
            }
          );
        }

        // Remove skills
        for (const skillId of skillsToRemove) {
          const skill = processSkills.find(s => s.skill_parameter_ref === skillId);
          if (skill && skill.id) {
            await apiClient.delete(
              `/prime-artifacts/${process.prime_artifact_ref}/processes/${process.id}/skills/${skill.id}`
            );
          }
        }

        // Reload skills
        await loadProcessSkills();
      } catch (error) {
        console.error('Error updating process skills:', error);
      }
    }
  };

  // Helper to render a key-value table for JSON fields
  const renderKeyValueTable = (obj: Record<string, unknown>) => (
    <table className={styles.jsonTable}>
      <tbody>
        {Object.entries(obj || {}).map(([key, value]) => (
          <tr key={key}>
            <td className={styles.jsonKey}>{key}</td>
            <td className={styles.jsonValue}>{JSON.stringify(value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  // --- Unified Transition Table State & Sorting ---
  const [sortColumn, setSortColumn] = useState<'artifact' | 'input' | 'type' | 'output'>(
    'artifact'
  );
  const [sortAsc, setSortAsc] = useState(true);

  // Build transition rows from processCentricView
  let transitionRows: Array<{
    artifact_name: string;
    input_state: ProcessStateView;
    transition_type: ProcessStateView['transition_type'];
    output_state: ProcessStateView;
  }> = [];
  if (processCentricView) {
    // Map transitions by transition_id for pairing input/output
    const inputByTransition: Record<string, ProcessStateView> = {};
    const outputByTransition: Record<string, ProcessStateView> = {};
    processCentricView.input_states.forEach((st) => {
      inputByTransition[st.transition_id] = st;
    });
    processCentricView.output_states.forEach((st) => {
      outputByTransition[st.transition_id] = st;
    });
    // Build rows for transitions present in both
    transitionRows = Object.keys({ ...inputByTransition, ...outputByTransition }).map((tid) => {
      const input = inputByTransition[tid];
      const output = outputByTransition[tid];
      const transition_type = input
        ? input.transition_type
        : output
          ? output.transition_type
          : ('' as ProcessStateView['transition_type']);
      const artifact_name = input ? input.artifact_name : output ? output.artifact_name : '';
      const emptyState: ProcessStateView = {
        id: '',
        name: '',
        description: '',
        state_type: '' as ProcessStateView['state_type'],
        default_transition_type: '' as ProcessStateView['default_transition_type'],
        transition_parameters: {},
        validation_rules: {},
        demand_type: '' as ProcessStateView['demand_type'],
        demand_flow_type: '' as ProcessStateView['demand_flow_type'],
        prime_artifact_ref: '',
        artifact_name: artifact_name,
        transition_id: tid,
        transition_type: transition_type,
        transition_name: '',
        transition_description: '',
      };
      return {
        artifact_name,
        input_state: (input ? input : emptyState) as ProcessStateView,
        transition_type,
        output_state: (output ? output : emptyState) as ProcessStateView,
      } as {
        artifact_name: string;
        input_state: ProcessStateView;
        transition_type: ProcessStateView['transition_type'];
        output_state: ProcessStateView;
      };
    });
  }
  // Sorting
  transitionRows.sort((a, b) => {
    let cmp = 0;
    if (sortColumn === 'artifact') {
      cmp = a.artifact_name.localeCompare(b.artifact_name);
    } else if (sortColumn === 'input') {
      cmp = a.input_state.name.localeCompare(b.input_state.name);
    } else if (sortColumn === 'type') {
      cmp = a.transition_type.localeCompare(b.transition_type);
    } else if (sortColumn === 'output') {
      cmp = (a.output_state?.name || '').localeCompare(b.output_state?.name || '');
    }
    return sortAsc ? cmp : -cmp;
  });

  const handleSort = (col: typeof sortColumn) => {
    if (sortColumn === col) {
      setSortAsc((a) => !a);
    } else {
      setSortColumn(col);
      setSortAsc(true);
    }
  };

  return (
    <div className={styles.primeProcessDetails}>
      <div className={styles.primeProcessForm}>
        <div className={styles.primeProcessHeader}>
          <h2>{process.name}</h2>
          {!isEditing && (
            <div className={styles.primeProcessActions}>
              <button className={`${styles.primeProcessButton} ${styles.primary}`} onClick={onEdit}>
                Edit
              </button>
              <button className={`${styles.primeProcessButton} ${styles.danger}`} onClick={onDelete}>
                Delete
              </button>
            </div>
          )}
        </div>

        <div className={styles.primeProcessFormGroup}>
          <label>Name</label>
          <input
            type="text"
            value={process.name || ''}
            onChange={(e) => onChange('name', e.target.value)}
            disabled={!isEditing}
          />
        </div>

        <div className={styles.primeProcessFormGroup}>
          <label>Description</label>
          <textarea
            value={process.description || ''}
            onChange={(e) => onChange('description', e.target.value)}
            disabled={!isEditing}
          />
        </div>

        <div className={styles.primeProcessFormGroup}>
          <label>Process Type</label>
          <select
            value={process.process_type || ProcessType.DEMAND}
            onChange={(e) => onChange('process_type', e.target.value as ProcessType)}
            disabled={!isEditing}
          >
            <option value={ProcessType.DEFAULT}>Default (Unassigned)</option>
            <option value={ProcessType.DEMAND}>Demand</option>
            <option value={ProcessType.SUPPLY}>Supply</option>
            <option value={ProcessType.DELIVERY}>Delivery</option>
            <option value={ProcessType.FINANCE}>Finance</option>
            <option value={ProcessType.HR}>HR</option>
            <option value={ProcessType.IT}>IT</option>
            <option value={ProcessType.STRATEGY}>Strategy</option>
            <option value={ProcessType.IMPROVEMENT}>Improvement</option>
            <option value={ProcessType.QUALITY}>Quality</option>
            <option value={ProcessType.COMPLIANCE}>Compliance</option>
            <option value={ProcessType.RESEARCH}>Research</option>
            <option value={ProcessType.MARKETFIT}>MarketFit</option>
            <option value={ProcessType.FITFINDER}>FitFinder</option>
          </select>
        </div>

        <div className={styles.primeProcessFormGroup}>
          <label>Demand Flow Type</label>
          <select
            value={process.demand_flow_type || DemandFlowType.DRUM}
            onChange={(e) => onChange('demand_flow_type', e.target.value as DemandFlowType)}
            disabled={!isEditing}
          >
            <option value={DemandFlowType.DRUM}>Drum</option>
            <option value={DemandFlowType.PUSH}>Push</option>
            <option value={DemandFlowType.PULL}>Pull</option>
          </select>
        </div>

        {/* Required Skills Section */}
        <div className={styles.primeProcessFormGroup}>
          <label>Required Skills</label>
          <div className={styles.skillsSection}>
            {loadingSkills ? (
              <div className={styles.loadingText}>Loading skills...</div>
            ) : (
              <SkillSelector
                value={selectedSkills}
                onChange={handleSkillsChange}
                multiSelect={true}
                placeholder="Select required skills for this process..."
                disabled={!isEditing}
              />
            )}
            {selectedSkills.length > 0 && (
              <div className={styles.skillsInfo}>
                {selectedSkills.length} skill{selectedSkills.length !== 1 ? 's' : ''} required
              </div>
            )}
          </div>
        </div>

        {/* Collapsible JSON Section */}
        <div className={styles.jsonSection}>
          <button
            className={styles.jsonSectionToggle}
            type="button"
            onClick={onToggleJsonSection}
            aria-expanded={!jsonSectionCollapsed}
          >
            {jsonSectionCollapsed ? 'Show' : 'Hide'} Advanced JSON Fields
          </button>
          {!jsonSectionCollapsed && (
            <div className={styles.jsonSectionContent}>
              <div className={styles.jsonFieldGroup}>
                <h4>Process Parameters</h4>
                {renderKeyValueTable(process.process_parameters || {})}
              </div>
              <div className={styles.jsonFieldGroup}>
                <h4>Demand Calculation Rules</h4>
                {renderKeyValueTable(process.demand_calculation_rules || {})}
              </div>
              <div className={styles.jsonFieldGroup}>
                <h4>Validation Rules</h4>
                {renderKeyValueTable(process.validation_rules || {})}
              </div>
              <div className={styles.jsonFieldGroup}>
                <h4>Improvement Parameters</h4>
                {renderKeyValueTable(process.improvement_parameters || {})}
              </div>
            </div>
          )}
        </div>

        {/* Unified Transition Table */}
        {viewLoading && <div>Loading process-centric view...</div>}
        {viewError && <div className={styles.error}>{viewError}</div>}
        {processCentricView && (
          <div className={styles.processCentricSection}>
            <h4>Process Transitions</h4>
            <table className={styles.transitionTable}>
              <thead>
                <tr>
                  <th onClick={() => handleSort('artifact')} style={{ cursor: 'pointer' }}>
                    Artifact Name {sortColumn === 'artifact' ? (sortAsc ? '▲' : '▼') : ''}
                  </th>
                  <th onClick={() => handleSort('input')} style={{ cursor: 'pointer' }}>
                    Input State {sortColumn === 'input' ? (sortAsc ? '▲' : '▼') : ''}
                  </th>
                  <th onClick={() => handleSort('type')} style={{ cursor: 'pointer' }}>
                    Transition Type {sortColumn === 'type' ? (sortAsc ? '▲' : '▼') : ''}
                  </th>
                  <th onClick={() => handleSort('output')} style={{ cursor: 'pointer' }}>
                    Output State {sortColumn === 'output' ? (sortAsc ? '▲' : '▼') : ''}
                  </th>
                </tr>
              </thead>
              <tbody>
                {transitionRows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className={styles.empty}>
                      No transitions
                    </td>
                  </tr>
                ) : (
                  transitionRows.map((row) => (
                    <tr key={row.input_state.transition_id}>
                      <td>{row.artifact_name}</td>
                      <td>{row.input_state.name}</td>
                      <td>{row.transition_type}</td>
                      <td>{row.output_state?.name || ''}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {isEditing && (
          <div className={styles.primeProcessActions}>
            <button className={`${styles.primeProcessButton} ${styles.primary}`} onClick={onSave}>
              Save
            </button>
            <button className={`${styles.primeProcessButton} ${styles.secondary}`} onClick={onCancel}>
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
